import { CONDITION_SOURCE } from "@builder/utils/constants";
import { Dispatch, ReactNode, SetStateAction, createContext, useState } from "react";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { customersApi } from "@builder/store/slices/dataApiSlices/customersApiSlice";
import { ordersApi } from "@builder/store/slices/dataApiSlices/ordersApiSlice";
import { productsApi } from "@builder/store/slices/dataApiSlices/productsApiSlice";
import { rolesApi } from "@builder/store/slices/dataApiSlices/rolesApiSlice";
import { usersApi } from "@builder/store/slices/dataApiSlices/userApiSlice";
import type { Customer } from "@builder/types/Customer.type";
import type { Order } from "@builder/types/Order.type";
import type { Product } from "@builder/types/Product.type";
import type { Role } from "@builder/types/Role.type";
import type { User } from "@builder/types/Users.type";

interface ConditionDataProviderProps {
    children: ReactNode;
}

interface ConditionsData {
    isLoading: boolean;
    products: Record<string, string> | null;
    users: Record<string, string> | null;
    roles: Record<string, string> | null;
    customers: Record<string, string> | null;
    orders: Record<string, string> | null;
    offset: number;
    setOffset: Dispatch<SetStateAction<number>>;
    hasMore: boolean;
    setHasMore: Dispatch<SetStateAction<boolean>>;
    getAPITargetOptions: (source: string, offset: number) => Promise<ReturnResponse | null>;
    searchOptions: (source: string, searchValue: string) => Promise<string[] | null>;
    getItemById: (source: string, ids: string) => Promise<string | null>;
    fetchedSelection: Record<string, string> | null;
}

type ApiItem = (Product | User | Role | Customer | Order) & { _id: string };

export interface ReturnResponse {
    total: number;
    hits: Record<string, string>;
}

export const ConditionsDataContext = createContext<ConditionsData>({
    products: null,
    users: null,
    roles: null,
    customers: null,
    orders: null,
    isLoading: false,
    offset: 0,
    setOffset: () => {},
    hasMore: false,
    setHasMore: () => {},
    getAPITargetOptions: async () => null,
    searchOptions: async () => null,
    getItemById: async () => null,
    fetchedSelection: null
});

const BATCH_SIZE = 20;
const START = 0;

function itemToDisplayKeyAndId(key: string, item: ApiItem): { displayKey: string; id: string } {
    const id = item._id;
    if (key === CONDITION_SOURCE.CUSTOMER || key === CONDITION_SOURCE.USER) {
        const userOrCustomer = item as User | Customer;
        const firstName = userOrCustomer.firstName ?? "";
        const lastName = userOrCustomer.lastName ?? "";
        return { displayKey: `${firstName} ${lastName}`.trim() || id, id };
    }
    if (key === CONDITION_SOURCE.ORDER) {
        const order = item as Order;
        return { displayKey: order.receiptId ?? "Unknown receipt number", id };
    }
    const withName = item as Product | Role;
    return { displayKey: (withName as { name?: string }).name ?? id, id };
}

function itemsToHits(key: string, items: ApiItem[]): Record<string, string> {
    return items.reduce<Record<string, string>>((acc, item) => {
        const { displayKey, id } = itemToDisplayKeyAndId(key, item);
        acc[displayKey] = id;
        return acc;
    }, {});
}

export const ConditionsDataProvider = ({ children }: ConditionDataProviderProps) => {
    const dispatch = useAppDispatch();
    const [products, setProducts] = useState<Record<string, string> | null>(null);
    const [users, setUsers] = useState<Record<string, string> | null>(null);
    const [roles, setRoles] = useState<Record<string, string> | null>(null);
    const [customers, setCustomers] = useState<Record<string, string> | null>(null);
    const [orders, setOrders] = useState<Record<string, string> | null>(null);
    const [fetchedSelection, setFetchedSelection] = useState<Record<string, string>>({});
    const [offset, setOffset] = useState(START);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const companyId = useAppSelector(state => state.storeData.data?.company?._id);
    const organizationId = useAppSelector(state => state.storeData.data?.company?.organizationId);

    const searchOptions = async (source: string, searchValue: string): Promise<string[] | null> => {
        let key = source.split(" ")[0].toLowerCase();
        if (key === CONDITION_SOURCE.ACTIVE) {
            key = source.split(" ")[1].toLowerCase();
        }

        if (!companyId) return null;

        const baseParams = {
            companyId,
            limit: BATCH_SIZE,
            search: searchValue,
            ...(organizationId && { organizationId })
        };
        const useSkip = key === CONDITION_SOURCE.USER || key === CONDITION_SOURCE.ROLE;
        const params = useSkip ? { ...baseParams, skip: START } : { ...baseParams, offset: START };

        try {
            let items: ApiItem[] = [];
            if (key === CONDITION_SOURCE.PRODUCT) {
                const data = await dispatch(productsApi.endpoints.getProducts.initiate(params)).unwrap();
                items = data as ApiItem[];
            } else if (key === CONDITION_SOURCE.USER) {
                const data = await dispatch(usersApi.endpoints.getUsers.initiate(params)).unwrap();
                items = data as ApiItem[];
            } else if (key === CONDITION_SOURCE.ROLE) {
                const data = await dispatch(rolesApi.endpoints.getRoles.initiate(params)).unwrap();
                items = data as ApiItem[];
            } else if (key === CONDITION_SOURCE.CUSTOMER) {
                const data = await dispatch(customersApi.endpoints.getCustomers.initiate(params)).unwrap();
                items = data as ApiItem[];
            } else if (key === CONDITION_SOURCE.ORDER) {
                const data = await dispatch(ordersApi.endpoints.getOrders.initiate(params)).unwrap();
                items = data as ApiItem[];
            } else {
                return null;
            }

            const transformedData = itemsToHits(key, items);
            return Object.keys(transformedData);
        } catch {
            return null;
        }
    };

    const getAPITargetOptions = async (source: string, offsetVal: number): Promise<ReturnResponse | null> => {
        if (!source) return null;

        let key = source.split(" ")[0].toLowerCase();
        if (key === CONDITION_SOURCE.ACTIVE) {
            key = source.split(" ")[1].toLowerCase();
        }

        if (key.includes("cart")) return null;
        if (key.includes("new")) return null;
        if (!companyId) return null;

        const baseParams = {
            companyId,
            limit: BATCH_SIZE,
            ...(organizationId && { organizationId })
        };

        try {
            setIsLoading(true);

            const useSkip = key === CONDITION_SOURCE.USER || key === CONDITION_SOURCE.ROLE;
            const queryParams = useSkip
                ? { ...baseParams, skip: offsetVal }
                : { ...baseParams, offset: offsetVal };

            let items: ApiItem[] = [];
            if (key === CONDITION_SOURCE.PRODUCT) {
                const data = await dispatch(productsApi.endpoints.getProducts.initiate(queryParams)).unwrap();
                items = data as ApiItem[];
            } else if (key === CONDITION_SOURCE.USER) {
                const data = await dispatch(usersApi.endpoints.getUsers.initiate(queryParams)).unwrap();
                items = data as ApiItem[];
            } else if (key === CONDITION_SOURCE.ROLE) {
                const data = await dispatch(rolesApi.endpoints.getRoles.initiate(queryParams)).unwrap();
                items = data as ApiItem[];
            } else if (key === CONDITION_SOURCE.CUSTOMER) {
                const data = await dispatch(customersApi.endpoints.getCustomers.initiate(queryParams)).unwrap();
                items = data as ApiItem[];
            } else if (key === CONDITION_SOURCE.ORDER) {
                const data = await dispatch(ordersApi.endpoints.getOrders.initiate(queryParams)).unwrap();
                items = data as ApiItem[];
            } else {
                setIsLoading(false);
                return null;
            }

            const transformedData = itemsToHits(key, items);

            switch (key) {
                case CONDITION_SOURCE.USER:
                    setUsers(prev => ({ Null: "null", ...(prev || {}), ...transformedData }));
                    break;
                case CONDITION_SOURCE.PRODUCT:
                    setProducts(prev => ({ Null: "null", ...(prev || {}), ...transformedData }));
                    break;
                case CONDITION_SOURCE.ROLE:
                    setRoles(prev => ({ Null: "null", ...(prev || {}), ...transformedData }));
                    break;
                case CONDITION_SOURCE.CUSTOMER:
                    setCustomers(prev => ({ Null: "null", ...(prev || {}), ...transformedData }));
                    break;
                case CONDITION_SOURCE.ORDER:
                    setOrders(prev => ({ Null: "null", ...(prev || {}), ...transformedData }));
                    break;
                default:
                    break;
            }

            setIsLoading(false);
            return { total: items.length, hits: transformedData };
        } catch (error) {
            console.error("Error:", error);
            setIsLoading(false);
            return null;
        }
    };

    const getItemById = async (source: string, ids: string): Promise<string | null> => {
        if (!source || !companyId) return null;

        const idsArr = ids.split(", ");
        if (idsArr.includes("null")) return "null";

        let key = source.split(" ")[0].toLowerCase();
        if (key === CONDITION_SOURCE.ACTIVE) {
            key = source.split(" ")[1].toLowerCase();
        }
        if (key.includes("cart")) return null;

        const params = { companyId, ids: idsArr, ...(organizationId && { organizationId }) };

        try {
            let data: ApiItem[] = [];
            if (key === CONDITION_SOURCE.PRODUCT) {
                data = (await dispatch(productsApi.endpoints.getProductsByIds.initiate(params)).unwrap()) as ApiItem[];
            } else if (key === CONDITION_SOURCE.USER) {
                data = (await dispatch(usersApi.endpoints.getUsersByIds.initiate(params)).unwrap()) as ApiItem[];
            } else if (key === CONDITION_SOURCE.ROLE) {
                data = (await dispatch(rolesApi.endpoints.getRolesByIds.initiate(params)).unwrap()) as ApiItem[];
            } else if (key === CONDITION_SOURCE.CUSTOMER) {
                data = (await dispatch(customersApi.endpoints.getCustomersByIds.initiate(params)).unwrap()) as ApiItem[];
            } else if (key === CONDITION_SOURCE.ORDER) {
                data = (await dispatch(ordersApi.endpoints.getOrdersByIds.initiate(params)).unwrap()) as ApiItem[];
            } else {
                return null;
            }

            const rec: Record<string, string> = {};
            const keys = idsArr
                .map(id => {
                    const item = data.find(hit => hit._id === id);
                    if (!item) return "";
                    const { displayKey, id: itemId } = itemToDisplayKeyAndId(key, item);
                    rec[displayKey] = itemId;
                    return displayKey;
                })
                .filter(k => k !== "");

            setFetchedSelection(prev => ({ ...prev, ...rec }));
            return keys.join(", ");
        } catch {
            return null;
        }
    };

    return (
        <ConditionsDataContext.Provider
            value={{
                isLoading,
                getAPITargetOptions,
                getItemById,
                searchOptions,
                products,
                users,
                customers,
                orders,
                roles,
                offset,
                setOffset,
                hasMore,
                setHasMore,
                fetchedSelection
            }}
        >
            {children}
        </ConditionsDataContext.Provider>
    );
};
